import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/api';

/**
 * Windows Hello / passkey self-enrollment for the signed-in admin (REQ #21).
 * The account must have "Enable Windows Hello login" ticked in Edit User first;
 * otherwise the register-options call returns 403 and we show the reason.
 */

const Card = styled.div`
  background: #fff; border: 1px solid var(--border-subtle); border-radius: 0.5rem;
  padding: 1.1rem 1.35rem; margin-bottom: 1.25rem;
`;
const Title = styled.h3`margin: 0 0 0.35rem; color: var(--ve-ink-800); font-size: 1.05rem;`;
const Sub = styled.p`margin: 0 0 0.9rem; color: var(--ve-ink-600); font-size: 0.88rem;`;
const List = styled.ul`margin: 0 0 0.9rem; padding-left: 1.1rem; color: var(--ve-ink-800); font-size: 0.9rem;`;
const Row = styled.div`display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap;`;
const Btn = styled.button`
  padding: 0.55rem 1rem; border-radius: 0.25rem; font-weight: 600; font-size: 0.9rem; cursor: pointer;
  border: none; color: #fff; background: var(--act-primary); &:hover { filter: brightness(0.94); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const RemoveBtn = styled.button`
  background: none; border: none; color: var(--act-danger); cursor: pointer; font-size: 0.82rem; font-weight: 600;
  &:hover { text-decoration: underline; }
`;
const Note = styled.div`
  margin-top: 0.7rem; font-size: 0.85rem;
  color: ${({ $err }) => ($err ? 'var(--act-danger)' : '#1e8449')};
`;

const WebAuthnSetup = () => {
  const supported = typeof window !== 'undefined' && !!window.PublicKeyCredential;
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      setCreds(await authService.webauthnListCredentials());
    } catch (e) {
      // 401/403 simply means no passkeys / not enabled — leave the list empty.
      setCreds([]);
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const deviceName = window.navigator.platform || 'This device';
      await authService.webauthnRegister(deviceName);
      setMsg('Windows Hello is set up. You can now sign in with it.');
      await load();
    } catch (e) {
      setErr(e.message || 'Could not set up Windows Hello.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await authService.webauthnDeleteCredential(id);
      await load();
    } catch (e) {
      setErr(e.message || 'Could not remove the passkey.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Title>Windows Hello sign-in</Title>
      <Sub>
        Register this device&apos;s Windows Hello (or Touch ID / a passkey) so you can sign in
        without a password. Your account must have Windows Hello enabled by an administrator.
      </Sub>

      {!supported && <Note $err>This browser does not support WebAuthn / Windows Hello.</Note>}

      {loaded && creds.length > 0 && (
        <List>
          {creds.map((c) => (
            <li key={c.id}>
              {c.deviceName || 'Passkey'}
              {c.lastUsedAt ? ` — last used ${new Date(c.lastUsedAt).toLocaleDateString()}` : ''}
              {'  '}
              <RemoveBtn type="button" onClick={() => remove(c.id)} disabled={busy}>Remove</RemoveBtn>
            </li>
          ))}
        </List>
      )}

      <Row>
        <Btn type="button" onClick={enroll} disabled={busy || !supported}>
          {busy ? 'Working…' : creds.length > 0 ? 'Add another device' : 'Set up Windows Hello'}
        </Btn>
      </Row>

      {msg && <Note>{msg}</Note>}
      {err && <Note $err>{err}</Note>}
    </Card>
  );
};

export default WebAuthnSetup;
